//
// # Firebase Email Link Authentication
//
// Client-side Firebase auth module for the IDE.
// Handles email link sign-in, token management, and auth state.
//

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  initializeAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type Auth,
  type User,
  type Unsubscribe,
} from "firebase/auth";
import { logFromBrowser } from "./browserLog";

// Firebase config injected by AlphaServer into window.__GENALPHA_CONFIG__
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

// localStorage key for storing the email used to send the sign-in link.
// Needed to complete sign-in when the user clicks the link (possibly in the same browser).
const EMAIL_STORAGE_KEY = "genalpha_email_for_signin";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

/**
 * Initialize Firebase with config from server-injected window.__GENALPHA_CONFIG__.
 * Uses initializeAuth() instead of getAuth() to avoid "Component auth has not
 * been registered yet" errors in webpack environments.
 */
export function initFirebase(config: FirebaseConfig): void {
  if (app) return;
  app = initializeApp({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
  });
  auth = initializeAuth(app, {
    persistence: browserLocalPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
  });
}

/**
 * Send a sign-in email link to the given address.
 * Stores the email in localStorage so we can complete sign-in when the user returns.
 */
export async function sendLoginLink(email: string): Promise<boolean> {
  if (!auth) {
    console.error("sendLoginLink: Firebase auth not initialized");
    return false;
  }

  const actionCodeSettings = {
    url: window.location.origin,
    handleCodeInApp: true,
  };

  // Fire-and-forget observability ping so AlphaServer logs the attempt
  // before we hand off to Firebase. Captures the email + domain so we can
  // answer "did anyone from $company try to sign in" via Cloud Logging
  // without hitting the Firebase Auth user list (which only shows
  // completed sign-ins). Intentionally not awaited — we don't want a
  // logging hiccup to block the user's actual sign-in.
  //
  // PII: full email lands in Cloud Logging by design — see the matching
  // note in AlphaServer/src/auth.ts where the post-Firebase deny is
  // logged. Revisit if compliance posture tightens.
  const at = email.lastIndexOf("@");
  logFromBrowser("auth-link-requested", "user requested email sign-in link", {
    email,
    domain: at >= 0 ? email.slice(at + 1).toLowerCase() : "",
  });

  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  localStorage.setItem(EMAIL_STORAGE_KEY, email);
  return true;
}

/**
 * Attempt to complete sign-in if the current URL is a sign-in email link.
 * Returns the signed-in user, or null if the URL is not a sign-in link.
 */
export async function completeSignIn(): Promise<User | null> {
  if (!auth) return null;

  if (!isSignInWithEmailLink(auth, window.location.href)) {
    return null;
  }

  // Retrieve the email from localStorage (stored when link was sent)
  let email = localStorage.getItem(EMAIL_STORAGE_KEY);
  if (!email) {
    // Edge case: user opened the link in a different browser.
    // Prompt for email so we can complete sign-in.
    email = window.prompt("Please enter your email to confirm sign-in:");
    if (!email) return null;
  }

  try {
    const result = await signInWithEmailLink(auth, email, window.location.href);
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    return result.user;
  } finally {
    // Always clear the sign-in link from the URL — even on failure — so a
    // refresh doesn't replay a spent/expired `oobCode` and re-throw, leaving
    // the user stuck on a blank screen.
    history.replaceState(null, "", window.location.pathname);
  }
}

/**
 * Sign in with Google popup.
 */
export async function signInWithGoogle(): Promise<User | null> {
  if (!auth) {
    console.error("signInWithGoogle: Firebase auth not initialized");
    return null;
  }
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

/**
 * Get a fresh Firebase ID token for the current user.
 * Returns cached token if still valid, auto-refreshes if near expiry.
 * Returns null if no user is signed in.
 */
export async function getCurrentToken(): Promise<string | null> {
  if (!auth?.currentUser) return null;
  return auth.currentUser.getIdToken();
}

/**
 * Get the current Firebase user (synchronous, may be null before auth state resolves).
 */
export function getCurrentUser(): User | null {
  return auth?.currentUser ?? null;
}

/**
 * Subscribe to auth state changes.
 * Callback fires immediately with current state, then on each change.
 */
export function onAuthStateChange(
  callback: (user: User | null) => void
): Unsubscribe | null {
  if (!auth) return null;
  return onAuthStateChanged(auth, callback);
}

/**
 * Sign out the current user and clear stored email.
 */
export async function signOut(): Promise<void> {
  if (!auth) return;
  localStorage.removeItem(EMAIL_STORAGE_KEY);
  await firebaseSignOut(auth);
}
